import {
  HubBuildState,
  type HubExampleDefinition,
  RequestParams,
} from '@robovision/hub'
import {
  addOrReplaceBy,
  defaultSort,
  groupBy,
  HttpStatusCode,
  naturalSort,
  sortByKey,
} from '@robovision/rvai-utils'
import { useIsFetching } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import {
  coerce,
  rcompare,
} from 'semver'
import {
  computed,
  reactive,
  toRefs,
} from 'vue'
import type { HubAlgorithmDefinition } from '@robovision/hub'
import { Algorithm } from '~domain/algorithm/Algorithm'
import { AlgorithmDefinition } from '~domain/algorithm/AlgorithmDefinition'
import type { JobV2 } from '~domain/jobV2/Job'
import { hubApi } from '~services/api'
import { features } from '~services/features'
import { queryClient } from '~services/query-client'
import {
  acceptStatus,
} from '~utils/api'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'
import { minByComparator } from '~utils/minByComparator'


// TODO: Remove this once the measurement decisions are fully implemented
function filterAlgorithmDefinitionByFeatureFlag (
  definition: HubAlgorithmDefinition,
): HubAlgorithmDefinition {
  if (features.measurementDecisionsEnabled || !definition.parameterDefinitions) return definition

  const MEASUREMENT_DECISION_PARAMETER_IDS = [
    'ai.robovision.algorithms.YoloInstanceSegmentation.parameters.InferenceParameters',
    'ai.robovision.algorithms.semantic_segmentation.parameters.InferenceParameters',
  ]

  const parameterDefinitions = definition.parameterDefinitions.map(paramDef => {
    if (
      !MEASUREMENT_DECISION_PARAMETER_IDS.includes(paramDef.id) ||
      !paramDef.schema
    ) return paramDef

    const HIDDEN_PARAMETERS = [
      'area_measurement',
      'detection_size_measurement_enabled',
    ]

    const HIDDEN_GROUPS = [
      'detection_size_measurement_params',
    ]

    return {
      ...paramDef,
      schema: {
        ...paramDef.schema,
        fields: paramDef.schema.fields.filter(f => !HIDDEN_PARAMETERS.includes(f.key)),
        groups: paramDef.schema.groups?.filter(g => !HIDDEN_GROUPS.includes(g.id)),
      },
    }
  })

  return {
    ...definition,
    parameterDefinitions,
  }
}

function getAlgorithmDefinitionFromApi ({
  id, version,
}: {
  id: string
  version: string
}) {
  return queryClient.fetchQuery({
    queryFn: () => hubApi.algorithms.getAlgorithmDefinition(
      id,
      version,
      { original: false },
      {
        ...RETHROW_ERROR_AFTER_HANDLING,
        validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
      },
    ),
    queryKey: ['hubApi', 'aglorithms', id, version, 'definition'],
    staleTime: query => query.state.error != null ? 0 : Infinity,
  })
}

function getAlgorithmFromApi ({
  id, version,
}: {
  id: string
  version: string
}) {
  return queryClient.fetchQuery({
    queryFn: () => hubApi.algorithms.getAlgorithm(id, version, RETHROW_ERROR_AFTER_HANDLING),
    queryKey: ['hubApi', 'algorithms', id, version],
    staleTime: query => query.state.error != null ? 0 : Infinity,
  })
}

type GetAlgorithmsQuery = Parameters<(typeof hubApi.algorithms.listAlgorithms)>[0]

function getAlgorithmsFromApi (payload: GetAlgorithmsQuery) {
  return queryClient.fetchQuery({
    queryFn: () => {
      const params: RequestParams = {
        ...RETHROW_ERROR_AFTER_HANDLING,
      }

      if (window.Cypress) {
        // On CI there is a rare bug in connection between frontend and backend.
        // It causes the request to never complete.
        // Therefore we want to setup a timeout and retry policy to handle this case.
        // Failed job: https://git.robovision.ai/RoboVision/rvai/-/jobs/5177468
        // We set it to 8s to be able to retry 2 times within the cypress timeout of 30s.
        params.timeout = 8_000
        params['axios-retry'] = {
          retries: 2,
          shouldResetTimeout: true,
          retryCondition: (error) => {
            return error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK
          },
          retryDelay: () => 1000, // 1s
          onRetry: (retryCount, error) => {
            console.warn(`Axios Retry #${retryCount}. Error: ${error.message}`)
          },
        }
      }

      return hubApi.algorithms.listAlgorithms(
        {
          includeDeleted: true,
          includeExtensions: true,
        },
        params,
      )
    },
    queryKey: ['hubApi', 'algorithms', payload],
  })
}

type CoerciableToVersion = Parameters<typeof coerce>[0]
function latestVersionFirst (
  a: CoerciableToVersion,
  b: CoerciableToVersion,
): number {
  const aVer = coerce(a)
  const bVer = coerce(b)

  // invalid versions are last
  if (aVer == null && bVer == null) return 0
  if (aVer == null) return 1
  if (bVer == null) return -1

  return rcompare(aVer, bVer)
}

export const useAlgorithmsStore = defineStore('algorithms', () => {
  const state = reactive({
    algorithmDefinitions: [],
    algorithmExtensions: [],
    algorithms: [],
    isAlgorithmsDefinitionsLoaded: false,
    isAlgorithmsLoaded: false,
  }) as {
    algorithmDefinitions: AlgorithmDefinition[]
    algorithmExtensions: Algorithm[]
    algorithms: Algorithm[]
    isAlgorithmsDefinitionsLoaded: boolean
    isAlgorithmsLoaded: boolean
  }

  const algorithmsLoading = useIsFetching(
    {
      queryKey: ['hubApi', 'algorithms'],
    },
    queryClient,
  )
  const loading = computed(() => algorithmsLoading.value > 0)

  const getters = {
    getAlgorithm (id: string | undefined, version: string | undefined): Algorithm | undefined {
      if (!id || !version) return undefined
      return state.algorithms
        .find(a => a.id === id && a.version === version)
    },

    getAlgorithmDefinition (id: string | undefined, version: string | undefined): AlgorithmDefinition | undefined {
      if (!id || !version) return undefined
      return state.algorithmDefinitions
        .find(a => a.id === id && a.version === version)
    },

    getAlgorithmDefinitionByJob (job: JobV2): AlgorithmDefinition | undefined {
      return state.algorithmDefinitions
        .find(a => a.id === job.algorithmId && a.version === job.algorithmVersion)
    },

    getAlgorithmLatestVersion (id: string): string | undefined {
      return state.algorithms
        .filter(a => a.id === id)
        .map(a => a.version)
        .sort(naturalSort)
        .pop()
    },

    getAllAlgorithmDefinitions (): AlgorithmDefinition[] {
      return state.algorithmDefinitions
    },

    getAllExampleDefinitions (): HubExampleDefinition[] {
      return state.algorithmDefinitions.flatMap(a => a.exampleDefinitions as HubExampleDefinition[])
    },

    getAvailableAlgorithms (): Algorithm[] {
      // only show algorithms that have at least 1 example definitions
      const filtered = state.algorithms
        .filter(algorithm => {
          return (
            algorithm.buildState === HubBuildState.READY &&
            this.getAlgorithmDefinition(algorithm.id, algorithm.version)?.exampleDefinitions?.length
          )
        })

      const grouped = groupBy(filtered, 'id')

      // get the latest version of which algorithm
      const latests = [...grouped.values()]
        .map(arr => minByComparator(arr, (a, b) => latestVersionFirst(a.version, b.version)))
        .filter(Boolean)

      return sortByKey(latests, 'name')
    },

    getInstalledAlgorithm (id: string | undefined, version: string | undefined): Algorithm | undefined {
      if (!id || !version) return undefined
      return state.algorithms
        .concat(state.algorithmExtensions)
        .find(a => a.id === id && a.version === version)
    },

    getInstalledAlgorithms (): Algorithm[] {
      return [...state.algorithms, ...state.algorithmExtensions]
        .filter((a) => a.buildState !== HubBuildState.DELETED)
        .sort((a, b) => defaultSort(a.name, b.name) || latestVersionFirst(a.version, b.version))
    },

    isAlgorithmAvailable (id: string, version: string): boolean {
      const algorithm = state.algorithms.find((a) => a.id === id && a.version === version)
      return !!algorithm && algorithm.buildState === HubBuildState.READY
    },

    isInstalledAlgorithmAvailable (id: string, version: string): boolean {
      const algorithm = state.algorithms
        .concat(state.algorithmExtensions)
        .find((a) => a.id === id && a.version === version)
      return !!algorithm && algorithm.buildState === HubBuildState.READY
    },
  }

  const actions = {
    async deleteAlgorithm (id: string, version: string, deleteLocalState = true): Promise<void> {
      await hubApi.algorithms.deleteAlgorithm(id, version)

      if (deleteLocalState) {
        this.deleteAlgorithmLocalState(id, version)
      }
    },

    deleteAlgorithmLocalState (id: string, version: string): void {
      let index = state.algorithms.findIndex(a => a.id === id && a.version === version)
      if (index >= 0) {
        state.algorithms.splice(index, 1)
      }
      index = state.algorithmExtensions.findIndex(a => a.id === id && a.version === version)
      if (index >= 0) {
        state.algorithmExtensions.splice(index, 1)
      }
    },

    async isAlgorithmInstalled (id: string, version: string): Promise<boolean> {
      const response = await getAlgorithmDefinitionFromApi({
        id,
        version,
      })

      return response.status === HttpStatusCode.OK
    },

    async loadAlgorithm (id: string, version: string): Promise<Algorithm> {
      const algorithm = getters.getAlgorithm(id, version)
      if (algorithm) return algorithm

      const { data } = await getAlgorithmFromApi({
        id,
        version,
      })
      const newAlgorithm = Algorithm.fromResponse(data)

      if (data.extension) {
        state.algorithmExtensions = addOrReplaceBy(
          state.algorithmExtensions,
          newAlgorithm,
          a => a.id === id && a.version === version,
        )
      } else {
        state.algorithms = addOrReplaceBy(
          state.algorithms,
          newAlgorithm,
          a => a.id === id && a.version === version,
        )
      }

      return newAlgorithm
    },

    async loadAlgorithmDefinition ({
      force,
      id,
      skipInvalid,
      version,
    }: {
      force?: boolean
      id: string
      skipInvalid?: boolean
      version: string
    }): Promise<AlgorithmDefinition | undefined> {
      const definition = getters.getAlgorithmDefinition(id, version)
      if (definition && !force) return definition

      const response = await getAlgorithmDefinitionFromApi({
        id,
        version,
      })
      if (response.status === HttpStatusCode.NOT_FOUND) return undefined

      let algorithmDefinition: AlgorithmDefinition
      try {
        const data = filterAlgorithmDefinitionByFeatureFlag(response.data)
        algorithmDefinition = AlgorithmDefinition.fromResponse(data)
      } catch (e) {
        if (skipInvalid) {
          console.log('Failed to load algorithm definition ' + id + '. Skipping')
          return undefined
        } else {
          throw e
        }
      }

      state.algorithmDefinitions = addOrReplaceBy(
        state.algorithmDefinitions,
        algorithmDefinition,
        a => a.key === algorithmDefinition.key,
      )

      return algorithmDefinition
    },

    async loadAlgorithms (): Promise<void> {
      const payload = {
        includeDeleted: true,
        includeExtensions: true,
      }
      const { data: algorithms } = await getAlgorithmsFromApi(payload)
      const latestAlgorithms = new Map(
        [...groupBy(algorithms.items, 'id')
          .entries()]
          .map(([algorithmId, algorithms]) =>
            [
              algorithmId,
              minByComparator(
                algorithms,
                (a, b) => latestVersionFirst(a.version, b.version),
              ),
            ]),
      )

      // show the latest name, assuming it is the correct one
      const updatedAlgorithms = algorithms.items.map(a => ({
        ...a,
        name: latestAlgorithms.get(a.id)?.name ?? '',
      }))

      state.algorithms = updatedAlgorithms.filter(a => !a.extension).map(Algorithm.fromResponse)
      state.algorithmExtensions = updatedAlgorithms.filter(a => a.extension).map(Algorithm.fromResponse)
      state.isAlgorithmsLoaded = true
    },

    async loadAllAlgorithmDefinitions (force: boolean = false): Promise<void> {
      const algorithmPromises = state.algorithms
        .map(a => this.loadAlgorithmDefinition({
          force,
          id: a.id,
          version: a.version,
        }))
        .concat(state.algorithmExtensions.map(e => this.loadAlgorithmDefinition({
          force,
          id: e.id,
          skipInvalid: true,
          version: e.version,
        })))
      await Promise.all(algorithmPromises)
      state.isAlgorithmsDefinitionsLoaded = true
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(state),
    loading,
    ...actions,
    ...getters,
  }
})
